// /var/www/oszczedzam/client/src/components/dashboard/ExpenseChart.js
import React, { useState, useEffect } from 'react';
import { 
  Box, Paper, Typography, FormControl, InputLabel, 
  Select, MenuItem, useTheme, Tabs, Tab, alpha
} from '@mui/material';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, 
  AreaChart, Area, LineChart, Line
} from 'recharts';

function ExpenseChart({ transactions }) {
  const theme = useTheme();
  const [chartData, setChartData] = useState([]);
  const [chartPeriod, setChartPeriod] = useState('month');
  const [chartType, setChartType] = useState('bar');
  
  useEffect(() => {
    prepareChartData();
  }, [transactions, chartPeriod]);
  
  // Przygotuj dane do wykresu
  const prepareChartData = () => {
    if (!transactions || transactions.length === 0) {
      setChartData([]);
      return;
    }
    
    let data = [];
    const currentDate = new Date();
    
    if (chartPeriod === 'month') {
      // Dane dla aktualnego miesiąca (dzień po dniu)
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      
      // Utwórz tablicę z danymi dla każdego dnia
      data = Array.from({ length: daysInMonth }, (_, i) => {
        const day = i + 1;
        return {
          name: `${day}`,
          przychody: 0,
          wydatki: 0,
          saldo: 0
        };
      });
      
      // Przypisz transakcje do odpowiednich dni
      transactions.forEach(transaction => {
        const transactionDate = new Date(transaction.date);
        // Tylko transakcje z bieżącego miesiąca
        if (transactionDate.getMonth() === month && transactionDate.getFullYear() === year) {
          const day = transactionDate.getDate() - 1; // Indeks zaczyna się od 0
          if (transaction.amount > 0) {
            data[day].przychody += transaction.amount;
          } else {
            data[day].wydatki += Math.abs(transaction.amount);
          }
          data[day].saldo = data[day].przychody - data[day].wydatki;
        }
      });
    } else if (chartPeriod === 'year') {
      // Dane dla aktualnego roku (miesiąc po miesiącu)
      const monthNames = ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'];
      const year = currentDate.getFullYear();
      
      // Utwórz tablicę z danymi dla każdego miesiąca
      data = monthNames.map(month => ({
        name: month,
        przychody: 0,
        wydatki: 0,
        saldo: 0
      }));
      
      // Przypisz transakcje do odpowiednich miesięcy
      transactions.forEach(transaction => {
        const transactionDate = new Date(transaction.date);
        // Tylko transakcje z bieżącego roku
        if (transactionDate.getFullYear() === year) {
          const month = transactionDate.getMonth();
          if (transaction.amount > 0) {
            data[month].przychody += transaction.amount;
          } else {
            data[month].wydatki += Math.abs(transaction.amount);
          }
          data[month].saldo = data[month].przychody - data[month].wydatki;
        }
      });
    }
    
    setChartData(data);
  };
  
  // Obsługa zmiany okresu
  const handlePeriodChange = (event) => {
    setChartPeriod(event.target.value);
  };
  
  // Obsługa zmiany typu wykresu
  const handleChartTypeChange = (event, newValue) => {
    setChartType(newValue);
  };
  
  // Formatuj kwotę w tooltipie
  const formatTooltipValue = (value) => {
    return value.toLocaleString('pl-PL', { 
      style: 'currency', 
      currency: 'PLN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };
  
  // Niestandardowy tooltip dla wykresu
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            bgcolor: 'background.paper',
            p: 1.5,
            border: '1px solid',
            borderColor: theme.palette.divider,
            borderRadius: 1,
            boxShadow: theme.shadows[3]
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            {chartPeriod === 'month' ? `Dzień ${label}` : label}
          </Typography>
          {payload.map((entry, index) => (
            <Box key={`item-${index}`} sx={{ display: 'flex', alignItems: 'center', my: 0.5 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: entry.color,
                  mr: 1
                }}
              />
              <Typography variant="body2">
                {entry.name === 'przychody' ? 'Przychody' : entry.name === 'wydatki' ? 'Wydatki' : 'Saldo'}: 
                <Typography component="span" fontWeight="medium" sx={{ ml: 0.5 }}>
                  {formatTooltipValue(entry.value)}
                </Typography>
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };
  
  // Renderuj odpowiedni typ wykresu
  const renderChart = () => {
    switch (chartType) {
      case 'bar':
        return (
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.secondary, 0.2)} />
            <XAxis 
              dataKey="name" 
              tick={{ fill: theme.palette.text.secondary }}
              axisLine={{ stroke: theme.palette.divider }}
              tickLine={{ stroke: theme.palette.divider }}
            />
            <YAxis 
              tick={{ fill: theme.palette.text.secondary }} 
              axisLine={{ stroke: theme.palette.divider }}
              tickLine={{ stroke: theme.palette.divider }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ marginTop: 10 }} />
            <Bar 
              dataKey="przychody" 
              name="Przychody" 
              fill={theme.palette.success.main} 
              radius={[4, 4, 0, 0]}
              animationDuration={1500}
            />
            <Bar 
              dataKey="wydatki" 
              name="Wydatki" 
              fill={theme.palette.error.main} 
              radius={[4, 4, 0, 0]}
              animationDuration={1500}
            />
          </BarChart>
        );
      case 'line':
        return (
          <LineChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.secondary, 0.2)} />
            <XAxis 
              dataKey="name" 
              tick={{ fill: theme.palette.text.secondary }}
              axisLine={{ stroke: theme.palette.divider }}
              tickLine={{ stroke: theme.palette.divider }}
            />
            <YAxis 
              tick={{ fill: theme.palette.text.secondary }} 
              axisLine={{ stroke: theme.palette.divider }}
              tickLine={{ stroke: theme.palette.divider }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ marginTop: 10 }} />
            <Line 
              type="monotone" 
              dataKey="przychody" 
              name="Przychody" 
              stroke={theme.palette.success.main} 
              strokeWidth={2}
              dot={{ r: 3, fill: theme.palette.success.main, stroke: theme.palette.success.main }}
              activeDot={{ r: 5 }}
              animationDuration={1500}
            />
            <Line 
              type="monotone" 
              dataKey="wydatki" 
              name="Wydatki" 
              stroke={theme.palette.error.main} 
              strokeWidth={2}
              dot={{ r: 3, fill: theme.palette.error.main, stroke: theme.palette.error.main }}
              activeDot={{ r: 5 }}
              animationDuration={1500}
            />
            <Line 
              type="monotone" 
              dataKey="saldo" 
              name="Saldo" 
              stroke={theme.palette.primary.main} 
              strokeWidth={2}
              dot={{ r: 3, fill: theme.palette.primary.main, stroke: theme.palette.primary.main }}
              activeDot={{ r: 5 }}
              animationDuration={1500}
            />
          </LineChart>
        );
      case 'area':
        return (
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.secondary, 0.2)} />
            <XAxis 
              dataKey="name" 
              tick={{ fill: theme.palette.text.secondary }}
              axisLine={{ stroke: theme.palette.divider }}
              tickLine={{ stroke: theme.palette.divider }}
            />
            <YAxis 
              tick={{ fill: theme.palette.text.secondary }} 
              axisLine={{ stroke: theme.palette.divider }}
              tickLine={{ stroke: theme.palette.divider }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ marginTop: 10 }} />
            <Area 
              type="monotone" 
              dataKey="przychody" 
              name="Przychody" 
              stroke={theme.palette.success.main} 
              fill={alpha(theme.palette.success.main, 0.2)}
              activeDot={{ r: 5 }}
              strokeWidth={2}
              animationDuration={1500}
            />
            <Area 
              type="monotone" 
              dataKey="wydatki" 
              name="Wydatki" 
              stroke={theme.palette.error.main} 
              fill={alpha(theme.palette.error.main, 0.2)}
              activeDot={{ r: 5 }}
              strokeWidth={2}
              animationDuration={1500}
            />
          </AreaChart>
        );
      default:
        return null;
    }
  };
  
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3, 
        mb: 4, 
        borderRadius: 3,
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        border: '1px solid',
        borderColor: alpha(theme.palette.primary.main, 0.1)
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight="medium">Przychody i wydatki</Typography>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="chart-period-label">Okres</InputLabel>
          <Select
            labelId="chart-period-label"
            id="chart-period"
            value={chartPeriod}
            label="Okres"
            onChange={handlePeriodChange}
          >
            <MenuItem value="month">Miesiąc</MenuItem>
            <MenuItem value="year">Rok</MenuItem>
          </Select>
        </FormControl>
      </Box>
      
      <Tabs 
        value={chartType} 
        onChange={handleChartTypeChange} 
        sx={{ mb: 2 }}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab value="bar" label="Słupkowy" />
        <Tab value="line" label="Liniowy" />
        <Tab value="area" label="Obszarowy" />
      </Tabs>
      
      <Box sx={{ height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}

export default ExpenseChart;